import { CSSProperties, MutableRefObject, forwardRef, useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';

import { Box, BoxProps, Flex, SlideFade, useToast } from '@chakra-ui/react';
import { Card, Contest } from 'models';

import { useLazyGetPlayerByIdQuery } from 'api/api';

import { Back } from './Back';
import { Face } from './Face';

const defaultAspectRatio = 220 / 330;

type Props = BoxProps & {
  card: Card;
  width?: string;
  detailed?: boolean;
  styles?: {
    image?: CSSProperties;
    info?: CSSProperties;
    firstName?: CSSProperties;
    lastName?: CSSProperties;
  };
  contest?: Contest | null;
  mode?: 'builder' | 'default';
  changeStateTrigger?: string;
};

const PlayerCard = forwardRef<HTMLDivElement, Props>(
  ({ contest, card, width, styles, detailed, mode = 'default', changeStateTrigger, ...props }, ref) => {
    const [calcWidth, setCalcWidth] = useState(width ?? 'auto');
    const [aspectRatio, setAspectRatio] = useState<number>(width ? parseInt(width) / 220 : defaultAspectRatio);
    const [isOriginalImageUploaded, setIsOriginalImageUploaded] = useState(false);
    const container = useRef<HTMLDivElement>();
    const [isFlipped, setIsFlipped] = useState(false);
    const toast = useToast();

    const [showBack, setShowBack] = useState(false);
    const [
      getPlayer,
      {
        data: player,
        isSuccess: isPlayerLoadedSuccessfully,
        isFetching: isPlayerLoading,
        isError: isPlayerLoadedError,
      },
    ] = useLazyGetPlayerByIdQuery();

    const safePlayerData = isPlayerLoadedError ? null : player;

    const handleFlip = async () => {
      if (mode === 'builder') {
        ReactGA.event({
          category: 'PlayerCard',
          action: 'tap_player_card_' + contest?.league,
          label: `PlayerCard flipped`,
        });
        setIsFlipped((prev) => !prev);
        if (!isFlipped) {
          await getPlayer({ playerId: card.playerSrId });
        }
      }
    };

    useEffect(() => {
      if (isPlayerLoadedError) {
        toast({
          duration: 3000,
          isClosable: true,
          position: 'bottom',
          render: ({ onClose }) => {
            return (
              <Flex
                alignItems="center"
                justifyContent="center"
                p="5px 18px !important"
                backgroundColor="#111111"
                borderRadius="10px"
                gap="10px"
                w="min-content"
                whiteSpace="nowrap"
                margin="0 auto 10px"
                onClick={onClose}
              >
                <Box>Player data not found</Box>
              </Flex>
            );
          },
        });
      }
    }, [isPlayerLoadedError]);

    useEffect(() => {
      if (mode === 'builder') {
        setIsFlipped(false);
      }
    }, [card]);

    useEffect(() => {
      setIsFlipped(false);
    }, [changeStateTrigger]);

    useEffect(() => {
      if (width) return;
      if (container.current && card) {
        const resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const newHeight = entry.contentRect.height;
            const newWidth = aspectRatio * newHeight;
            setAspectRatio(newWidth / 200);
            setCalcWidth(`${newWidth}px`);
          }
        });

        resizeObserver.observe(container.current);
      }
    }, [width]);

    useEffect(() => {
      setIsOriginalImageUploaded(false);
      if (!card) return;
      const img = new Image();
      img.src = card?.headshotThumbnails?.original ?? '';
      img.onload = () => {
        setIsOriginalImageUploaded(true);
      };
    }, [card]);

    const combinedRef = (element: HTMLDivElement) => {
      if (ref && typeof ref === 'object' && 'current' in ref) {
        (ref as MutableRefObject<HTMLDivElement | null>).current = element;
      }
      container.current = element;
    };

    const headshotSrc = isOriginalImageUploaded ? card?.headshotThumbnails?.original : card?.headshotThumbnails?.small;

    useEffect(() => {
      let timeoutId: ReturnType<typeof setTimeout>;

      if (!isFlipped) {
        timeoutId = setTimeout(() => {
          setShowBack(false);
        }, 600);
      } else {
        setShowBack(true);
      }

      return () => {
        clearTimeout(timeoutId);
      };
    }, [isFlipped]);

    if (!card) return null;

    return (
      <>
        <Box
          pos="relative"
          w={calcWidth}
          ref={combinedRef}
          onClick={handleFlip}
          style={{
            perspective: '2000px',
            transformStyle: 'preserve-3d',
          }}
          {...props}
          borderRadius={`${16 * aspectRatio}px`}
          boxShadow={isFlipped || isPlayerLoadedSuccessfully ? 'none !important' : `inherit`}
          transition="opacity 0.3s ease-in-out"
        >
          <Box
            bg={props.bg ? props.bg : 'white'}
            borderRadius={`${16 * aspectRatio}px`}
            boxShadow={`0px ${3 * aspectRatio}px ${20 * aspectRatio}px 0px ${card?.modifierPoints ? 'transparent' : 'rgba(0,0,0,0.5)'}`}
            position="relative"
            width="100%"
            height="100%"
            transition="transform 0.6s ease-in-out"
            style={{
              transformStyle: 'preserve-3d',
            }}
            transform={`rotateY(${isFlipped ? 180 : 0}deg)`}
          >
            <Box
              px={`${8 * aspectRatio}px`}
              py={`${6 * aspectRatio}px`}
              position="absolute"
              top="0"
              left="0"
              width="100%"
              height="100%"
              style={{ backfaceVisibility: 'hidden' }}
            >
              <Face
                detailed={detailed}
                styles={styles}
                isOriginalImageUploaded={isOriginalImageUploaded}
                aspectRatio={aspectRatio}
                card={card}
                hasInjuryStatus={card.playerInjuries}
                contest={contest}
                league={contest?.league}
              />
            </Box>
            {showBack && (
              <Box
                position="absolute"
                top="0"
                left="0"
                zIndex="10"
                width="100%"
                height="100%"
                style={{ backfaceVisibility: 'hidden' }}
                transform="rotateY(180deg)"
              >
                <Back
                  isLoading={isPlayerLoading}
                  detailed={detailed}
                  calcWidth={calcWidth}
                  player={safePlayerData}
                  card={card}
                  styles={styles}
                  aspectRatio={aspectRatio}
                  isOriginalImageUploaded={isOriginalImageUploaded}
                  league={contest?.league}
                />
              </Box>
            )}
          </Box>
        </Box>
      </>
    );
  }
);

export default PlayerCard;
